import { getFocalPointStyles, parserOptions } from "@insite/client-framework/Common/BasicSelectors";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import parseImageUrl from "@insite/client-framework/Common/Utilities/parseImageUrl";
import Zone from "@insite/client-framework/Components/Zone";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import Button, { ButtonPresentationProps } from "@insite/mobius/Button";
import { GridContainerProps } from "@insite/mobius/GridContainer";
import Modal, { ModalPresentationProps } from "@insite/mobius/Modal";
import Typography from "@insite/mobius/Typography";
import TypographyStyle from "@insite/mobius/Typography/TypographyStyle";
import getColor from "@insite/mobius/utilities/getColor";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import parse from "html-react-parser";
import React, { useEffect, useState } from "react";
import { css } from "styled-components";

const enum fields {
    buttonText = "buttonText",
    closeButton = "closeButton",
    closeButtonText = "closeButtonText",
    backgroundImage = "backgroundImage",
    image = "image",
    responsiveImageBehavior = "responsiveImageBehavior",
    focalPoint = "focalPoint",
    bannerText = "bannerText",
}

interface Props extends WidgetProps {
    fields: {
        [fields.buttonText]: string;
        [fields.closeButton]: string;
        [fields.closeButtonText]: string;
        [fields.image]: string;
        [fields.backgroundImage]: boolean;
        [fields.focalPoint]:
            | "topLeft"
            | "topCenter"
            | "topRight"
            | "centerLeft"
            | "center"
            | "centerRight"
            | "bottomLeft"
            | "bottomCenter"
            | "bottomRight";
        [fields.responsiveImageBehavior]: "cover" | "center" | "prioritizeHeight" | "prioritizeWidth";
        [fields.bannerText]: string;
    };
}

export interface ModalWithDropZoneStyle {
    container?: GridContainerProps;
    modalWithDropZone?: ModalPresentationProps;
    modalButtonWrapper?: InjectableCss;
    buttonStyles?: ButtonPresentationProps;
}

export const containerStyles: ModalWithDropZoneStyle = {
    container: {
        css: css`
            border-radius: 7px;
            min-height: 200px;
            width: 100%;
            padding: 20px 0px;
            display: flex;
            flex-direction: column;
            align-items: center;
        `,
    },
    modalWithDropZone: {
        size: 650,
        cssOverrides: {
            modalContainer: css`
                > div {
                    width: fit-content;
                }
            `,
            modalBody: css`
                [data-test-selector="modal_ModalContent"] {
                    padding: 2px !important;
                    margin: auto !important;
                }
            `,
            modalTitle: css`
                padding: 0px;
                border: none;
                > ${TypographyStyle} {
                    color: ${getColor("secondary")};
                    font-size: 1rem;
                }
                #CloseModalButton {
                    position: absolute;
                    height: 33px;
                    top: 25px;
                    right: 25px;
                    width: 33px;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    background-color: #ffffff !important;
                    border: none !important;
                    min-height: 33px;
                    z-index: 9;
                    border: 1px solid #275aa8 !important;
                    > span {
                        color: #275aa8;
                        position: unset;
                        display: flex;
                        justify-content: center;
                        align-items: center;
                    }
                    svg {
                        height: 21px;
                        width: 21px;
                    }
                }
            `,
        },
    },
    modalButtonWrapper: {
        css: css`
            display: flex;
            justify-content: right;
            padding-top: 15px;
        `,
    },
    buttonStyles: {
        css: css`
            border-radius: 5px;
            background: linear-gradient(to bottom, #ffffff 0%, #f8f8f8 45%, #ececec 100%);
            border: 1px solid #d3d3d3;
            transition: all 0.3s ease;

            box-shadow:
                inset 0 1px 0 rgba(255, 255, 255, 0.9),
                0 2px 4px rgba(0, 0, 0, 0.15);

            padding: 0 28px;

            &:hover {
                background: #00539b;
                border: none;
                span {
                    color: #ffffff;
                }
            }

            span {
                font-size: 14px;
                color: #00539b;
                text-transform: uppercase;
            }
        `,
    },
};

const styles = containerStyles;

const ModalWithDropZone = ({ id, fields }: Props) => {
    const [isModalOpen, setIsModalOpen] = useState(false);
    const {
        buttonText,
        closeButton,
        closeButtonText,
        backgroundImage,
        image,
        focalPoint,
        responsiveImageBehavior,
        bannerText,
    } = fields;

    let responsiveImageBehaviorStyles = "";

    if (responsiveImageBehavior === "cover") {
        responsiveImageBehaviorStyles = "background-size: cover;";
    } else if (responsiveImageBehavior === "center") {
        responsiveImageBehaviorStyles = "background-size: contain;";
    } else if (responsiveImageBehavior === "prioritizeHeight") {
        responsiveImageBehaviorStyles = "background-size: auto 100%;";
    } else if (responsiveImageBehavior === "prioritizeWidth") {
        responsiveImageBehaviorStyles = "background-size: 100% auto;";
    }

    const containerStyles = {
        ...styles.container,
        css: css`
            ${styles.container?.css || ""}

            ${backgroundImage &&
            image &&
            `
            background-image: url(${parseImageUrl(image).url});
            background-repeat: no-repeat;
            background-position: center;

            ${responsiveImageBehaviorStyles}
            ${getFocalPointStyles(focalPoint)}
            ${bannerText ? "justify-content: space-between;" : "justify-content: flex-end;"}
        `}
        `,
    };

    return (
        <StyledWrapper {...containerStyles} data-test-selector="modalWithDropZoneWrapper">
            <Modal
                {...styles.modalWithDropZone}
                isOpen={isModalOpen}
                handleClose={() => setIsModalOpen(false)}
                data-test-selector="modalWithDropZone"
            >
                <Zone contentId={id} zoneName="Content1" />
                {closeButton && (
                    <StyledWrapper {...styles.modalButtonWrapper}>
                        <Button onClick={() => setIsModalOpen(false)}>{closeButtonText}</Button>
                    </StyledWrapper>
                )}
            </Modal>
            {backgroundImage && bannerText && (
                <Typography
                    css={css`
                        align-self: flex-end;
                        max-width: 150px;
                        color: white;
                        text-align: right;
                        margin-top: 35px;
                        margin-right: 18px;

                        p {
                            margin: 0;
                            font-size: 20px;
                        }
                    `}
                >
                    {parse(bannerText, parserOptions)}
                </Typography>
            )}

            {buttonText && (
                <Button {...styles.buttonStyles} onClick={() => setIsModalOpen(true)}>
                    {buttonText}
                </Button>
            )}
        </StyledWrapper>
    );
};

const widgetModule: WidgetModule = {
    component: ModalWithDropZone,
    definition: {
        displayName: "Modal With Drop Zone",
        group: "Common",
        fieldDefinitions: [
            {
                name: fields.buttonText,
                defaultValue: "Recent Articles",
                editorTemplate: "TextField",
                fieldType: "Translatable",
                sortOrder: 1,
            },
            {
                name: fields.closeButton,
                editorTemplate: "CheckboxField",
                defaultValue: true,
                fieldType: "General",
                sortOrder: 2,
            },
            {
                name: fields.closeButtonText,
                defaultValue: "Close",
                editorTemplate: "TextField",
                fieldType: "Translatable",
                sortOrder: 3,
            },
            {
                name: fields.backgroundImage,
                editorTemplate: "CheckboxField",
                defaultValue: false,
                fieldType: "General",
                sortOrder: 4,
            },
            {
                name: fields.image,
                editorTemplate: "ImagePickerField",
                defaultValue: "",
                fieldType: "General",
                sortOrder: 5,
                isVisible: item => item?.fields[fields.backgroundImage],
            },
            {
                name: fields.responsiveImageBehavior,
                editorTemplate: "DropDownField",
                fieldType: "General",
                defaultValue: "cover",
                sortOrder: 6,
                hideEmptyOption: true,
                isVisible: item => item?.fields[fields.backgroundImage],
                options: [
                    { displayName: "Cover", value: "cover" },
                    { displayName: "Center", value: "center" },
                    { displayName: "Prioritize height", value: "prioritizeHeight" },
                    { displayName: "Prioritize width", value: "prioritizeWidth" },
                ],
            },
            {
                name: fields.focalPoint,
                editorTemplate: "DropDownField",
                fieldType: "General",
                defaultValue: "center",
                sortOrder: 7,
                hideEmptyOption: true,
                isVisible: item => item?.fields[fields.backgroundImage],
                options: [
                    { displayName: "Top Left", value: "topLeft" },
                    { displayName: "Top Center", value: "topCenter" },
                    { displayName: "Top Right", value: "topRight" },
                    { displayName: "Center Left", value: "centerLeft" },
                    { displayName: "Center", value: "center" },
                    { displayName: "Center Right", value: "centerRight" },
                    { displayName: "Bottom Left", value: "bottomLeft" },
                    { displayName: "Bottom Center", value: "bottomCenter" },
                    { displayName: "Bottom Right", value: "bottomRight" },
                ],
            },
            {
                name: fields.bannerText,
                displayName: "Banner Text",
                editorTemplate: "RichTextField",
                fieldType: "Translatable",
                defaultValue: "",
                extendedConfig: { height: 100 },
                expandedToolbarButtons: {
                    moreMisc: {},
                    code: {},
                },
                sortOrder: 8,
                isVisible: item => item?.fields[fields.backgroundImage],
            },
        ],
    },
};

export default widgetModule;
